import {
  detectCumulativeSafetyAdjacentSignal,
  getSafetyContextState,
  getSafetyContextWindow,
} from "./safetyContext";

const SAFETY_FLAG_PATTERNS = [
  /\b(kill myself|end my life|take my own life|suicidal|suicide|self[-\s]?harm|harm myself|hurt myself)\b/i,
  /\b(i('| a)?m going to|i want to|i might|i could|i feel like|i plan to)\b.{0,40}\b(kill myself|end my life|die|self[-\s]?harm|harm myself|hurt myself)\b/i,
  /\b(i('| a)?m not safe|i am not safe|unsafe right now|in immediate danger)\b/i,
  /\b(everyone would be better off without me|better off without me)\b/i,
  /\b(i can'?t go on|i cannot go on|no reason to live|wish i (?:wasn'?t|were not) here)\b/i,
  /\b(i want to disappear|what'?s the point of living|life isn'?t worth living)\b/i,
  /\b(crisis|urgent help|immediate help)\b/i,
];

const HIGH_DISTRESS_WITH_RISK_PATTERNS = [
  /\b(depressed|hopeless|helpless|panic|panic attack)\b.{0,80}\b(not safe|can'?t cope|cannot cope|can'?t go on|cannot go on|danger|hurt|harm|alone)\b/i,
  /\b(not safe|can'?t cope|cannot cope|can'?t go on|cannot go on|danger|hurt|harm|alone)\b.{0,80}\b(depressed|hopeless|helpless|panic|panic attack)\b/i,
];

function isContextuallyNonActionableSafetyState(contextualState = {}) {
  return Boolean(
    contextualState.explicitDeescalation ||
    contextualState.latestClassification?.subject === "third_person" ||
    contextualState.latestClassification?.timeframe === "historical" ||
    contextualState.latestClassification?.classification === "quoted_or_informational"
  );
}

function getSummarySafetyReviewState(summary = {}) {
  if (!summary?.flagSuggested) return { practicalClarification: false, safetyReview: false };
  const themes = Array.isArray(summary.detectedThemes) ? summary.detectedThemes.join(" ") : "";
  const summaryText = `${themes} ${summary.summaryText || ""} ${summary.suggestedNextStep || ""}`.toLowerCase();
  return {
    practicalClarification: /\b(practical clarification|task support|going to the shops|meant the task|meant work|just tired)\b/i.test(summaryText),
    safetyReview: /\b(safety concern|safety-adjacent|self[-\s]?harm|suicid|human support|coach review|immediate safety|crisis|danger|fear of breaking down|going under|better off without me|don'?t want to be here|do not want to be here)\b/i.test(summaryText),
  };
}

export function detectSessionFlag(record) {
  if (!record) return false;
  const messages = Array.isArray(record.messages) ? record.messages : [];
  if (messages.length) {
    const contextualState = getSafetyContextState(messages);
    if (contextualState.activePersonalSafety) return true;
    const cumulativeWindow = getSafetyContextWindow(messages, contextualState);
    if (detectCumulativeSafetyAdjacentSignal(cumulativeWindow)) return true;
    if (isContextuallyNonActionableSafetyState(contextualState)) return false;
    const userMessageText = messages
      .slice(-6)
      .filter((m) => String(m?.role || m?.sender || "").toLowerCase() !== "assistant")
      .map((m) => m?.content || m?.text || "")
      .join(" ");
    if (HIGH_DISTRESS_WITH_RISK_PATTERNS.some((pattern) => pattern.test(userMessageText))) return true;
    const summarySafety = getSummarySafetyReviewState(record.sessionSummary);
    return summarySafety.safetyReview && !summarySafety.practicalClarification;
  }
  const summaryText = record.sessionSummary?.flagSuggested
    ? `${record.sessionSummary?.summaryText || ""} ${(record.sessionSummary?.detectedThemes || []).join(" ")}`
    : "";
  const hay = `${record.issueSummary || ""} ${record.summary || ""} ${summaryText}`
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .trim();
  if (!hay) return false;
  const fallbackState = getSafetyContextState([{ role: "user", content: hay }]);
  return fallbackState.activePersonalSafety ||
    SAFETY_FLAG_PATTERNS.some((pattern) => pattern.test(hay) && fallbackState.latestClassification.subject !== "third_person") ||
    HIGH_DISTRESS_WITH_RISK_PATTERNS.some((pattern) => pattern.test(hay));
}

export function hasActionableSessionFlagSuggestion(record) {
  if (!record) return false;
  if (detectSessionFlag(record)) return true;
  if (Array.isArray(record.messages) && record.messages.length) {
    const contextualState = getSafetyContextState(record.messages);
    if (isContextuallyNonActionableSafetyState(contextualState)) return false;
  }
  const summary = record.sessionSummary || {};
  const { practicalClarification, safetyReview } = getSummarySafetyReviewState(summary);
  return safetyReview && !practicalClarification;
}
